import { useGlobal } from "@/app/providers/use-global";
import { Button } from "@/components/ui/button";
import Icon from "@/components/ui/icon";
import { Tooltip } from "@/components/ui/tooltip";
import { useScreen } from "@/hooks/use-screen";
import { type PromptAndResponse, groq } from "@/hooks/use-send-message";
import { cn } from "@/lib/utils";
import { isFirefox, isMobileDevice } from "@/lib/utils/index";
import TextareaAutosize from "@/shared/components/textarea-autosize";
import { logError } from "@/shared/logging/logger";
import { useUser } from "@/shared/providers/user-provider";
import { CircleStopIcon } from "lucide-react";
import {
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
  memo,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { LiveAudioVisualizer } from "../live-audio-visualizer";

export type InputFormProps = {
  handleSubmit: (
    e?: FormEvent<HTMLFormElement> | KeyboardEvent<HTMLTextAreaElement>,
    injected?: string,
  ) => void;
  isInputDisabled: boolean;
  textDirection: string;
  promptInputRef?: RefObject<HTMLTextAreaElement>;
  handleInput: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  inputBuffer: string;
  setInputBuffer: (inputBuffer: string) => void;
  inlineLoginScrollRef: RefObject<HTMLDivElement>;
  chat: PromptAndResponse[];
  isTTSLoading: boolean;
  isTTSPlaying: boolean;
  stopTTS: () => void;
};

const InputForm = memo(
  ({
    handleSubmit,
    isInputDisabled,
    textDirection,
    promptInputRef,
    handleInput,
    inputBuffer,
    setInputBuffer,
    inlineLoginScrollRef,
    chat,
    isTTSLoading,
    isTTSPlaying,
    stopTTS,
  }: InputFormProps) => {
    const mimeType = useRef("");
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const [microphoneInteractionTimeStamp, setMicrophoneInteractionTimeStamp] =
      useState<number>(0);
    const [recordingTime, setRecordingTime] = useState(0);
    const {
      speechStatus,
      setShowLoginCardInHome,
      isIframe,
      currentModel,
      initialUserPrompt,
      isUserLoggedIn,
      sttSource,
    } = useGlobal();
    const { isMobile } = useScreen();
    const { buildRequestHeaders } = useUser();
    const [inputPlaceholderText, setInputPlaceholderText] =
      useState<string>("Try it");
    const askForMicrophonePermissionAndDefineAudioMimeType = () => {
      try {
        if (mimeType && mimeType.current === "") {
          let options = { mimeType: "" };
          if (MediaRecorder.isTypeSupported("audio/webm; codecs=opus")) {
            options = { mimeType: "audio/webm; codecs=opus" };
          } else if (MediaRecorder.isTypeSupported("audio/webm; codecs=vp9")) {
            options = { mimeType: "audio/webm; codecs=vp9" };
          } else if (MediaRecorder.isTypeSupported("audio/webm")) {
            options = { mimeType: "audio/webm" };
          } else if (MediaRecorder.isTypeSupported("audio/mp4; codecs=opus")) {
            options = { mimeType: "audio/mp4; codecs=opus" };
          } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
            options = { mimeType: "audio/mp4" };
          } else {
            toast.error(
              "Microphone error: no suitable mimetype found for this device",
            );
          }

          mimeType.current = options.mimeType;
        }
        // Ask for microphone permission prompt to show up on the initial page load
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then((stream) => {
            stream.getTracks().forEach((track) => track.stop());
          })
          .catch((_) => {
            toast.error("Microphone error: Error accessing the microphone");
          });
      } catch (_) {
        toast.error("Microphone error: Error accessing the microphone");
      }
    };

    function initializeMediaRecorder() {
      return new Promise((resolve, reject) => {
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then((stream) => {
            const mediaRecorder = new MediaRecorder(stream, {
              mimeType: mimeType.current,
            });

            mediaRecorderRef.current = mediaRecorder;

            const recordedChunks: Blob[] = [];

            mediaRecorder.ondataavailable = (event) => {
              if (event.data.size > 0) {
                recordedChunks.push(event.data);
              }
            };
            mediaRecorder.onstop = () => {
              const newAudioBlob = new Blob(recordedChunks, {
                type: mimeType.current,
              });
              speechStatus.setIsRecordingAudio(false);
              sendAudioForTranscription(newAudioBlob);

              stream.getTracks().forEach((track) => track.stop());
            };

            resolve(mediaRecorder);
          })
          .catch((error) => {
            reject(error);
          });
      });
    }

    const sendAudioForTranscription = async (newAudioBlob: Blob) => {
      speechStatus.setIsFetching(true);

      try {
        const audioFile = new File([newAudioBlob], "groq-home-audio.webm");

        const headers = await buildRequestHeaders();
        const headersWithApp = {
          ...headers,
          "Groq-App": "chat",
        } as Record<string, string>;
        const partner = localStorage.getItem("groq-partner");
        if (partner) {
          headersWithApp["Groq-Partner"] = partner;
        }

        const modelSttSourceMap = {
          english: "whisper-large-v3",
          arabic: "whisper-large-v3-arabic",
        };

        const transcription = await groq.audio.transcriptions.create(
          {
            file: audioFile,
            model: modelSttSourceMap[sttSource],
          },
          {
            headers: headersWithApp,
            maxRetries: 0,
          },
        );
        if (transcription.text === "") {
          speechStatus.setIsFetching(false);
          toast.error("No speech detected. Please try again.");
          return;
        }
        handleSubmit(undefined, transcription.text);
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      } catch (e: any) {
        toast.error("Error transcribing audio", {
          description: e?.error?.error?.message || "Unknown error",
        });
      }
      speechStatus.setIsFetching(false);
    };

    useEffect(() => {
      let timer: NodeJS.Timeout;
      if (speechStatus.isRecordingAudio && !speechStatus.isFetching) {
        timer = setInterval(() => {
          setRecordingTime((prevTime) => prevTime + 1);
        }, 1000);
      }
      if (!(speechStatus.isFetching || speechStatus.isRecordingAudio)) {
        setRecordingTime(0);
      }
      return () => clearInterval(timer);
    }, [speechStatus.isRecordingAudio, speechStatus.isFetching]);

    const formatTime = (seconds: number) => {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const isEnterKey = e.key === "Enter";
      const shiftOn = e.shiftKey;
      const isMobile = isMobileDevice();
      if (isEnterKey && !shiftOn && !isMobile) {
        handleSubmit();
        e.preventDefault();
      }
    };

    const startRecording = () => {
      if (!speechStatus.isRecordingAudio && mediaRecorderRef.current) {
        try {
          mediaRecorderRef.current.start();
          speechStatus.setIsRecordingAudio(true);
        } catch (error) {
          logError(error as Error);
          toast.error("Error starting the recording. Please try again.");
        }
      }
    };

    const stopRecording = () => {
      if (speechStatus.isRecordingAudio && mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
    };

    const cancelRecording = () => {
      if (speechStatus.isRecordingAudio && mediaRecorderRef.current) {
        speechStatus.setIsRecordingAudio(false);
        mediaRecorderRef.current.removeEventListener("stop", () => {
          // pass
        });
      }
    };

    const handleButtonDown = () => {
      if (!isUserLoggedIn) {
        isMobile &&
          inlineLoginScrollRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        setShowLoginCardInHome(true);
        return;
      }
      if (!(speechStatus.isRecordingAudio || speechStatus.isFetching)) {
        if (isFirefox()) {
          initializeMediaRecorder()
            .then(() => {
              setMicrophoneInteractionTimeStamp(Date.now());
              startRecording();
            })
            .catch(() => {
              toast.error(
                "Unable to access the microphone. Please enable microphone permissions to proceed.",
              );
            });
          return;
        }

        // @ts-ignore
        navigator.permissions.query({ name: "microphone" }).then((result) => {
          if (result.state === "granted") {
            initializeMediaRecorder().then(() => {
              setMicrophoneInteractionTimeStamp(Date.now());
              startRecording();
            });
          } else if (result.state === "prompt") {
            askForMicrophonePermissionAndDefineAudioMimeType();
          } else if (result.state === "denied") {
            toast.error(
              "Unable to access the microphone. Please enable microphone permissions to proceed.",
            );
          }
        });
      }
    };

    const handleButtonUp = () => {
      const diff = Date.now() - microphoneInteractionTimeStamp;
      if (diff > 300) {
        stopRecording();
      }
      setMicrophoneInteractionTimeStamp(Date.now());
    };

    const handleOnMouseDown = () => {
      if (isMobile) {
        return;
      }
      handleButtonDown();
    };

    const handleOnMouseUp = () => {
      if (isMobile) {
        return;
      }
      handleButtonUp();
    };

    const handleOnTouchStart = () => {
      handleButtonDown();
    };

    const handleOnTouchEnd = () => {
      handleButtonUp();
    };

    useEffect(() => {
      if (!isIframe) {
        return;
      }

      const timeout = setTimeout(() => {
        if (isInputDisabled && chat.length === 0 && !currentModel) {
          setInputPlaceholderText("Please reload the page.");
          window.parent.postMessage("refresh-window", "*");
        }
      }, 10000);

      return () => {
        clearTimeout(timeout);
      };
    }, []);

    useEffect(() => {
      if (initialUserPrompt) {
        setInputBuffer(initialUserPrompt);
      }
    }, [initialUserPrompt]);

    return (
      <>
        {speechStatus.isRecordingAudio &&
          mediaRecorderRef.current &&
          isMobile && (
            <div className="fixed inset-0 bottom-[86px] top-[68px] bg-background z-50 flex items-center justify-center">
              <LiveAudioVisualizer
                mediaRecorder={mediaRecorderRef.current}
                width={400}
                height={400}
                barColor="#F55036"
                gap={2}
                smoothingTimeConstant={0.8}
                circle={true}
              />
            </div>
          )}

        <form
          className={"md:p-0 flex items-end w-full max-w-[900px] h-max"}
          onSubmit={handleSubmit}
        >
          <div className="m-3 mb-6 px-2 flex w-full md:ring-[12px] ring-[#F8F8F8] dark:ring-[#242527] border border-mutedaccent md:border md:min-h-[52px] items-center rounded-xl dark:border-white">
            {/* Input */}
            {!(speechStatus.isRecordingAudio || speechStatus.isFetching) && (
              <TextareaAutosize
                className={cn(
                  "order-2 min-h-0 flex-1 resize-none rounded-none border-none bg-inherit px-3 py-2 text-base self-center focus-visible:ring-0 lg:focus-visible:ring-0 overflow-hidden",
                  isInputDisabled && "text-muted cursor-not-allowed",
                  textDirection === "rtl" && "text-right",
                )}
                ref={promptInputRef}
                autoFocus={!isMobileDevice()}
                id="chat"
                placeholder={inputPlaceholderText}
                rows={1}
                onInput={handleInput}
                onChange={(e) => setInputBuffer(e.target.value)}
                onKeyDown={handleKeyDown}
                value={inputBuffer}
                disabled={isInputDisabled}
              />
            )}

            {/* Visualizer */}
            {speechStatus.isRecordingAudio && mediaRecorderRef.current && (
              <div className="md:order-3 mr-auto md:ml-[11px] hidden md:block">
                <LiveAudioVisualizer
                  mediaRecorder={mediaRecorderRef.current}
                  width={200}
                  height={24}
                  barColor="#F55036"
                  gap={2}
                />
              </div>
            )}

            {/* Whisper Cancel */}
            {speechStatus.isRecordingAudio && isMobile && (
              <div className="relative size-10 order-1 ml-10">
                <Button
                  className={cn(
                    "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-14 p-2 cursor-pointer border bg-background border-mutedaccent dark:border-white rounded-full",
                    isInputDisabled && "cursor-not-allowed",
                  )}
                  type="button"
                  variant={"link"}
                  onClick={cancelRecording}
                  disabled={isInputDisabled}
                >
                  <Icon
                    className={cn(
                      "fill-muted dark:fill-white",
                      isInputDisabled && "fill-mutedaccent",
                    )}
                    icon="close"
                  />
                </Button>
              </div>
            )}

            {/* Mic */}
            <Tooltip
              trigger={
                <div
                  className={cn(
                    "relative size-10 order-2 md:order-1",
                    (speechStatus.isRecordingAudio ||
                      speechStatus.isFetching) &&
                      "ml-auto mr-10 md:m-0",
                  )}
                >
                  <Button
                    className={cn(
                      "p-0 rounded-full absolute size-14 md:size-[34px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[51] select-none",
                      speechStatus.isRecordingAudio &&
                        "bg-primaryaccent shadow-[0px_0px_20.7px_17px_rgba(245,80,54,0.6)] md:shadow-[0px_0px_8.9px_0px_rgba(245,80,54,0.6)]",
                      isInputDisabled && "cursor-not-allowed",
                    )}
                    type="button"
                    variant={"link"}
                    disabled={speechStatus.isFetching || isInputDisabled}
                    onMouseDown={handleOnMouseDown}
                    onMouseUp={handleOnMouseUp}
                    onTouchStart={handleOnTouchStart}
                    onTouchEnd={handleOnTouchEnd}
                  >
                    {speechStatus.isRecordingAudio && isMobile ? (
                      <Icon
                        size="md"
                        className="fill-white dark:fill-white pointer-events-none"
                        icon="send"
                      />
                    ) : (
                      <Icon
                        size="md"
                        className={cn(
                          "fill-none stroke-muted dark:stroke-white pointer-events-none",
                          isInputDisabled && "stroke-mutedaccent",
                          speechStatus.isRecordingAudio && "stroke-white",
                        )}
                        icon="microphone"
                      />
                    )}
                  </Button>
                </div>
              }
              providerProps={{ delayDuration: 200 }}
              contentProps={{ side: "top" }}
              className={cn(
                (speechStatus.isRecordingAudio || isMobile) && "hidden",
              )}
            >
              Whisper Large V3 - click to start/stop audio input
            </Tooltip>

            {/* Timer */}
            {speechStatus.isRecordingAudio && (
              <div className="order-3 md:order-2 md:ml-[11px] hidden md:block">
                {formatTime(recordingTime)}
              </div>
            )}

            {/* Send */}
            {isTTSLoading || isTTSPlaying ? (
              <Button
                className="order-last p-2 cursor-pointer ml-auto self-end my-[5px] text-primaryaccent"
                variant={"link"}
                onClick={stopTTS}
              >
                <CircleStopIcon />
              </Button>
            ) : (
              <Button
                className={cn(
                  "order-last p-2 cursor-pointer ml-auto self-end my-[5px]",
                  isInputDisabled && "cursor-not-allowed",
                  (speechStatus.isRecordingAudio || speechStatus.isFetching) &&
                    "hidden md:inline-flex",
                )}
                type="submit"
                variant={"link"}
                disabled={isInputDisabled}
              >
                <Icon
                  className={cn(
                    "fill-muted dark:fill-white",
                    isInputDisabled && "fill-mutedaccent",
                  )}
                  icon="send"
                />
              </Button>
            )}
          </div>
        </form>
      </>
    );
  },
);

InputForm.displayName = "InputForm";

export default InputForm;
